import {
  string as yupString,
  number as yupNumber,
  mixed as yupMixed,
} from 'yup';

import InputFormik from '../../shared/forms/input/inputFormik.component';
import SelectFormik from '../../shared/forms/select/sdkSelectFormik.component';
import {
  maxLength,
  required,
  yesNoFlag,
  nullable,
  integer,
  oneOfExisting,
  underscoredAlphaNumeric,
  permittedWhen,
  validateValueIsUnique,
} from '../../shared/forms/validators';
import { hideField } from '../../shared/forms/constants';
import { YES_FLAG, NO_FLAG } from '../../shared/constants';

import { EditCurrencyCodeAlertContent, EditHomeOfficeCodeAlertContent } from './editCurrencyCodeModalAlert.component';
import {
  ASSOCIATED_LE_COUNTRY_CODE,
} from './store/types';

const validateCompanyCode = validateValueIsUnique({
  optionsKey: 'companyCodesOptionsToValidate',
  message: 'Company Code already exists.',
  conditionFn: (option, selectedValue, context) =>
    option.value === selectedValue && option.companyCode !== context.parent.companyCode,
});

const homeOfficeCompanyCodeConnValid = (_, validator, validationContext) =>
  validator.test('homeOfficeCompanyCodeConnValid',
    'Home Office Company Code has to be in the same connection as Company code', function(selectedValue, context) {
      if (!selectedValue) {
        return nullable;
      }
      const foundCompanyCode = validationContext.homeOfficeCompanyCodeOptions.find(
        option => option.value === selectedValue
      );
      return foundCompanyCode && foundCompanyCode?.connName === context.parent.connName;
    });

const bsmFlagValid = (_, validator, validationContext) =>
  validator.test('bsmFlagValid',
    'BSM Flag for the Company Code and Home Office Company Code needs to match.', function(selectedValue, context) {
      if (!selectedValue) {
        return nullable;
      }
      if (!(validationContext && validationContext.homeOfficeCompanyCodeOptions)) {
        return true;
      }
      const found = validationContext.homeOfficeCompanyCodeOptions.find(
        element => element.value === context.parent.homeOfficeCompanyCode
      );
      if (!found) {
        return true;
      }
      return found.subledgerFlag === context.parent.subledgerFlag;
    });

const clientBsmFlagValid = (_, validator, validationContext) =>
  validator.test('clientBsmFlagValid',
    'Client BSM Flag is set to No.  Company Code BSM Flag must be No.', function(selectedValue) {
      if (!selectedValue) {
        return nullable;
      }

      const clientBsmFlagValue = validationContext.clientBsmFlag ? YES_FLAG : NO_FLAG;

      if (clientBsmFlagValue === NO_FLAG && selectedValue !== NO_FLAG) {
        return false;
      } else {
        return true;
      }
    });

const homeOfficeCountryCodeRequired = (label, validator) =>
  validator.when('homeOfficeCompanyCode', {
    is: homeOfficeCompanyCode => !!homeOfficeCompanyCode,
    then: yupString()
      .matches(/^US$/, `${label} must be US when Home Office Country Code is present`)
      .required(`${label} must be US when Home Office Country Code is present`),
    otherwise: yupString().nullable().matches(/^$/,
      `${label} must be empty when Home Office Company Code is not present`
    ),
  });

const isHomeOfficeCompanyCodePresent = ({ formik }) => !!formik.values.homeOfficeCompanyCode;

const associatedLEUSCountryCode = (label, validator) =>
  validator.when('associatedLeCountryCode', {
    is: associatedLeCountryCode => associatedLeCountryCode === ASSOCIATED_LE_COUNTRY_CODE,
    then: yupString()
      .nullable()
      .matches(/^N$/, `${label} C is invalid for a US Company Code`),
  });

const schema = [
  {
    name: 'companyCode',
    component: InputFormik,
    meta: { label: 'Company Code', optionsKey: 'companyCodesOptionsToValidate' },
    options: { disabledWhenEditing: true, yupType: yupString(), validationRules: [
      maxLength(13),
      required,
      underscoredAlphaNumeric,
      validateCompanyCode,
    ] },
    additionalProps: { required: true },
  },
  {
    name: 'ccDescription',
    component: InputFormik,
    meta: { label: 'Company Code Description' },
    options: { yupType: yupString(), validationRules: [maxLength(240)] },
  },
  {
    name: 'enabledFlag',
    component: SelectFormik,
    meta: { label: 'Enabled Flag', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [yesNoFlag, required] },
    additionalProps: { required: true },
  },
  {
    name: 'legalEntity',
    component: SelectFormik,
    meta: { label: 'Legal Entity', optionsKey: 'legalEntityOptions' },
    options: { yupType: yupString(), validationRules: [required, oneOfExisting('legalEntityOptions', { nullable: false })] },
    additionalProps: (formContext, formik) => ({
      required: true,
      virtualized: true,
      onSelect: value => {
        const legalEntity = formContext.legalEntityOptions.find(le => le.value === value);
        formik.setFieldValue('associatedLeCountryCode', legalEntity.countryCode);
      },
    }),
  },
  {
    name: 'businessUnit',
    component: SelectFormik,
    meta: { label: 'Business Unit', optionsKey: 'businessUnitOptions' },
    options: { yupType: yupString(), validationRules: [nullable, oneOfExisting('businessUnitOptions')] },
    additionalProps: { clearable: true, virtualized: true },
  },
  {
    name: 'chartName',
    component: SelectFormik,
    meta: { label: 'Chart Name', optionsKey: 'chartNameOptions' },
    options: { yupType: yupString(), validationRules: [nullable, required, oneOfExisting('chartNameOptions', { nullable: false })] },
    additionalProps: { clearable: true, virtualized: true, required: true },
  },
  {
    name: 'connName',
    component: SelectFormik,
    meta: { label: 'Connection Name', optionsKey: 'connectionNameOptions' },
    options: { yupType: yupString(), validationRules: [nullable, oneOfExisting('connectionNameOptions')] },
    additionalProps: { clearable: true, virtualized: true },
  },
  {
    name: 'associatedLeCountryCode',
    component: SelectFormik,
    meta: { label: 'Associated LE Country Code', optionsKey: 'countryCodeOptions' },
    options: { yupType: yupString(), validationRules: [required, oneOfExisting('countryCodeOptions', { nullable: false })] },
    additionalProps: { required: true, virtualized: true },
  },
  {
    name: 'associatedLeGoldId',
    component: InputFormik,
    meta: { label: 'Associated LE GOLD ID' },
    options: { yupType: yupString(), validationRules: [maxLength(6)] },
  },
  {
    name: 'functionalCurrency',
    component: SelectFormik,
    meta: { label: 'Functional Currency', optionsKey: 'currencyCodeOptions' },
    options: { yupType: yupString(), validationRules: [required, oneOfExisting('currencyCodeOptions', { nullable: false })] },
    additionalProps: { required: true, virtualized: true },
  },
  {
    name: 'overrideTaxCurrency',
    component: SelectFormik,
    meta: { label: 'Override Tax Currency', optionsKey: 'currencyCodeOptions' },
    options: { yupType: yupString(), validationRules: [nullable, oneOfExisting('currencyCodeOptions')] },
    additionalProps: { clearable: true, virtualized: true },
  },
  {
    name: 'editCurrencyCodeWarning',
    component: EditCurrencyCodeAlertContent,
    options: { readOnly: true },
    conditions: [
      {
        when: ({ formik, formContext }) => !(
          formContext.openForEdit
          && formik.values.subledgerFlag === YES_FLAG
          && formik.initialValues.overrideTaxCurrency !== formik.values.overrideTaxCurrency
        ),
        then: hideField,
      },
    ],
  },
  {
    name: 'homeOfficeCompanyCode',
    component: SelectFormik,
    meta: { label: 'Home Office Company Code', optionsKey: 'homeOfficeCompanyCodeOptions' },
    options: { yupType: yupString(), validationRules: [
      nullable,
      oneOfExisting('homeOfficeCompanyCodeOptions'),
      permittedWhen(
        ({ usShareholderFlag }) => !usShareholderFlag,
        'Shareholder is not permitted as Secondary Tax, when Branch has been selected.'
      ),
      homeOfficeCompanyCodeConnValid,
    ] },
    additionalProps: { clearable: true, virtualized: true },
    conditions: [{
      when: ({ formik }) =>
        (formik.values.associatedLeCountryCode === ASSOCIATED_LE_COUNTRY_CODE
         || formik.values.legalEntity === ''),
      then: {
        additionalProps: { disabled: true },
      },
    }],
  },
  {
    name: 'editHomeOfficeCodeWarning',
    component: EditHomeOfficeCodeAlertContent,
    options: { readOnly: true },
    conditions: [
      {
        when: ({ formik, formContext }) => !(
          formContext.openForEdit
          && formik.initialValues.homeOfficeCompanyCode !== formik.values.homeOfficeCompanyCode
          && formik.values.homeOfficeCompanyCode === null
          && formik.initialValues.homeOfficeCompanyCode !== ''
        ),
        then: hideField,
      },
    ],
  },
  {
    name: 'homeOfficeCountryCode',
    component: InputFormik,
    meta: { label: 'Home Office Country Code' },
    options: { yupType: yupMixed(), validationRules: [homeOfficeCountryCodeRequired] },
    conditions: [
      {
        when: isHomeOfficeCompanyCodePresent,
        then: {
          additionalProps: { required: true },
        },
      },
      {
        when: ({ formik }) =>
          (formik.values.associatedLeCountryCode === ASSOCIATED_LE_COUNTRY_CODE
          || formik.values.legalEntity === ''),
        then: {
          additionalProps: { disabled: true },
        },
      },
    ],
  },
  {
    name: 'recordFTC',
    component: SelectFormik,
    meta: { label: 'Record FTC', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [yesNoFlag, required] },
    additionalProps: { required: true },
  },
  {
    name: 'usShareholderFlag',
    component: SelectFormik,
    meta: { label: 'US Shareholder Flag', optionsKey: 'usShareholderFlagOptions' },
    options: {
      yupType: yupMixed(),
      validationRules: [
        associatedLEUSCountryCode,
        oneOfExisting('usShareholderFlagOptions'),
        permittedWhen(
          ({ homeOfficeCompanyCode }) => !homeOfficeCompanyCode,
          'Shareholder is not permitted as Secondary Tax, when Branch has been selected.'
        )],
    },
    additionalProps: { clearable: true },
    conditions: [
      {
        when: ({ formik }) => formik.values.legalEntity === '',
        then: {
          additionalProps: { disabled: true },
        },
      },
    ],
  },
  {
    name: 'editUsShareholderHomeOfficeWarning',
    component: EditHomeOfficeCodeAlertContent,
    options: { readOnly: true },
    conditions: [
      {
        when: ({ formik, formContext }) => !(
          formContext.openForEdit
          && formik.initialValues.usShareholderFlag !== formik.values.usShareholderFlag
          && formik.initialValues.usShareholderFlag === 'C'
          && formik.values.usShareholderFlag === null
        ),
        then: hideField,
      },
    ],
  },
  {
    name: 'businessCode',
    component: SelectFormik,
    meta: { label: 'Business Code', optionsKey: 'businessCodeOptions' },
    options: { yupType: yupString(), validationRules: [nullable, oneOfExisting('businessCodeOptions')] },
    additionalProps: { virtualized: true, clearable: true },
  },
  {
    name: 'ledgerId',
    component: InputFormik,
    meta: { label: 'Ledger ID' },
    options: { yupType: yupNumber(), validationRules: [integer] },
  },
  {
    name: 'ledgerShortName',
    component: InputFormik,
    meta: { label: 'Ledger Short Name' },
    options: { yupType: yupString(), validationRules: [maxLength(60)] },
  },
  {
    name: 'source',
    component: SelectFormik,
    meta: { label: 'Source', optionsKey: 'sourceOptions' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'subledgerFlag',
    component: SelectFormik,
    meta: { label: 'BSM Flag', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [yesNoFlag, required, bsmFlagValid, clientBsmFlagValid] },
    additionalProps: (formContext, formik) => {
      const props = { required: true };
      const clientBsmFlagValue = formContext.clientBsmFlag ? YES_FLAG : NO_FLAG;

      if (clientBsmFlagValue === NO_FLAG && !formik.values.subledgerFlag) {
        formik.setFieldValue('subledgerFlag', NO_FLAG);
      }

      return props;
    },
  },
  {
    name: 'statCalc',
    component: SelectFormik,
    meta: { label: 'STAT Calc Flag', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [yesNoFlag, required] },
    additionalProps: { required: true },
  },
  {
    name: 'statCurrency',
    component: SelectFormik,
    meta: { label: 'STAT Currency', optionsKey: 'currencyCodeOptions' },
    options: { yupType: yupString(), validationRules: [nullable, oneOfExisting('currencyCodeOptions')] },
    additionalProps: { clearable: true, virtualized: true },
  },
  {
    name: 'costCenter',
    component: InputFormik,
    meta: { label: 'Cost Center' },
    options: { yupType: yupString(), validationRules: [maxLength(6)] },
  },
  {
    name: 'project',
    component: InputFormik,
    meta: { label: 'Project' },
    options: { yupType: yupString(), validationRules: [maxLength(10)] },
  },
  {
    name: 'reference',
    component: InputFormik,
    meta: { label: 'Reference' },
    options: { yupType: yupString(), validationRules: [maxLength(6)] },
  },
  {
    name: 'productLine',
    component: InputFormik,
    meta: { label: 'Product Line' },
    options: { yupType: yupString(), validationRules: [maxLength(6)] },
  },
  {
    name: 'profitCenter',
    component: InputFormik,
    meta: { label: 'Profit Center' },
    options: { yupType: yupString(), validationRules: [maxLength(6)] },
  },
  {
    name: 'ledgerIdIn',
    component: InputFormik,
    meta: { label: 'Ledger ID In' },
    options: { yupType: yupNumber() },
  },
  {
    name: 'ledgerIdOut',
    component: InputFormik,
    meta: { label: 'Ledger ID Out' },
    options: { yupType: yupNumber() },
  },
  {
    name: 'leOverride',
    component: InputFormik,
    meta: { label: 'Legal Entity Override' },
    options: { yupType: yupString(), validationRules: [maxLength(6)] },
  },
  {
    name: 'ccOverride',
    component: InputFormik,
    meta: { label: 'Company Code Override' },
    options: { yupType: yupString(), validationRules: [maxLength(13)] },
  },
  {
    name: 'jeFlag',
    component: SelectFormik,
    meta: { label: 'Allow Journals', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [yesNoFlag] },
  },
  {
    name: 'updateBttFlag',
    component: SelectFormik,
    meta: { label: 'Update Book To Tax Flag', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [yesNoFlag] },
    conditions: [
      {
        when: ({ formContext }) => !(
          formContext.openForEdit
        ),
        then: hideField,
      },
    ],
  },
  {
    name: 'updateObligationsFlag',
    component: SelectFormik,
    meta: { label: 'Update Obligations Flag', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [yesNoFlag] },
    conditions: [
      {
        when: ({ formContext }) => !(
          formContext.openForEdit
        ),
        then: hideField,
      },
    ],
  },
];

export default schema;
